import { Color } from '@/GlobalStyles';
import { Platform, StatusBar, StyleSheet, TextStyle } from 'react-native';
import DropDownPicker, { DropDownPickerProps, ValueType } from 'react-native-dropdown-picker';
import React, { useEffect, useState } from 'react';
import { useTheme } from 'react-native-paper';

export type BaseDropDownPickerProps<T extends ValueType> = Omit<
  DropDownPickerProps<T>,
  'open' | 'setOpen' | 'multipleText'
> & {
  multipleText?:
    | {
        1: string;
        n: string;
      }
    | string;
  error?: boolean;
};

const BaseDropDownPicker = <T extends ValueType>(props: BaseDropDownPickerProps<T>) => {
  const { containerStyle: styleProp, labelProps, error = false, ...rest } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const theme = useTheme();

  // Sur Android, listMode='MODAL' affiche un Modal plein écran qui passe sous
  // la status bar (heure / batterie / notifs). On masque la status bar le temps
  // de l'ouverture pour éviter le chevauchement, puis on la réaffiche à la
  // fermeture.
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    if (dropdownOpen) {
      StatusBar.setHidden(true, 'fade');
      return () => {
        StatusBar.setHidden(false, 'fade');
      };
    }
  }, [dropdownOpen]);

  const contentStyle = (StyleSheet.flatten(styleProp) || {}) as TextStyle;
  const dropDownContainerStyle = {
    left:
      contentStyle?.paddingLeft ?? contentStyle?.paddingHorizontal ?? contentStyle?.padding ?? 0,
    right:
      contentStyle?.paddingRight ?? contentStyle?.paddingHorizontal ?? contentStyle?.padding ?? 0,
  };

  return (
    // @ts-ignore
    <DropDownPicker<T>
      props={{ activeOpacity: 1 }}
      open={dropdownOpen}
      setOpen={setDropdownOpen}
      itemSeparator
      labelStyle={{
        fontWeight: '500',
      }}
      labelProps={{
        numberOfLines: 1,
        ...labelProps,
      }}
      dropDownContainerStyle={[{ overflow: 'hidden' }, dropDownContainerStyle]}
      containerStyle={styleProp}
      style={
        error
          ? {
              borderColor: theme.colors.error,
            }
          : null
      }
      placeholderStyle={
        error
          ? {
              color: theme.colors.error,
            }
          : props.disabled
          ? {
              color: Color.MainGris,
            }
          : null
      }
      listMode={Platform.OS === 'android' ? 'MODAL' : 'FLATLIST'}
      modalProps={{
        animationType: 'slide',
        statusBarTranslucent: true,
        hardwareAccelerated: true,
      }}
      modalContentContainerStyle={{
        backgroundColor: Color.blanc,
      }}
      flatListProps={{
        nestedScrollEnabled: true,
        keyboardShouldPersistTaps: 'handled',
        showsVerticalScrollIndicator: true,
      }}
      searchTextInputProps={{
        autoCorrect: false,
      }}
      {...rest}
    />
  );
};

export default BaseDropDownPicker;
